/* eslint-disable react/display-name */

import Chooser from "~/components/shared/Chooser";
import Dropdown from "~/components/shared/Dropdown";
import PermissionDescription from "~/components/shared/PermissionDescription";
import PermissionSelectOptionDescriptions from "~/components/shared/PermissionSelectOptionDescriptions";

const MANAGE_BUILD_AND_READ = "MANAGE_BUILD_AND_READ";
const BUILD_AND_READ = "BUILD_AND_READ";
const READ_ONLY = "READ_ONLY";

type AccessLevelValue = typeof MANAGE_BUILD_AND_READ | typeof BUILD_AND_READ | typeof READ_ONLY;

interface TeamPipeline {
  accessLevel: string;
  permissions: {
    teamPipelineUpdate: {
      allowed: boolean;
    };
  };
}

interface AccessLevelProps {
  teamPipeline: TeamPipeline;
  onAccessLevelChange: (value: AccessLevelValue) => void;
  saving?: string | null;
}

interface DropdownWithChooserProps {
  saving?: string | null;
  selected: string;
  onAccessLevelChange: (value: AccessLevelValue) => void;
}

const getLabel = (value: string) => {
  switch (value) {
    case MANAGE_BUILD_AND_READ:
      return "Full Access";
    case BUILD_AND_READ:
      return "Build & Read";
    case READ_ONLY:
      return "Read Only";
  }
};

const DropdownWithChooser = ({
  saving,
  selected,
  onAccessLevelChange,
}: DropdownWithChooserProps) => (
  <Dropdown width={270}>
    <div className="underline-dotted inline-block cursor-pointer font-normal">
      {getLabel(selected)}
    </div>

    <Chooser selected={selected} onSelect={onAccessLevelChange}>
      <Chooser.SelectOption
        value={MANAGE_BUILD_AND_READ}
        saving={saving === MANAGE_BUILD_AND_READ}
        selected={selected === MANAGE_BUILD_AND_READ}
        label={getLabel(MANAGE_BUILD_AND_READ)}
        description={
          <PermissionSelectOptionDescriptions>
            <PermissionDescription allowed={true} permission="view and create builds" />
            <PermissionDescription allowed={true} permission="edit pipeline settings" />
          </PermissionSelectOptionDescriptions>
        }
      />
      <Chooser.SelectOption
        value={BUILD_AND_READ}
        saving={saving === BUILD_AND_READ}
        selected={selected === BUILD_AND_READ}
        label={getLabel(BUILD_AND_READ)}
        description={
          <PermissionSelectOptionDescriptions>
            <PermissionDescription allowed={true} permission="view and create builds" />
            <PermissionDescription allowed={false} permission="edit pipeline settings" />
          </PermissionSelectOptionDescriptions>
        }
      />
      <Chooser.SelectOption
        value={READ_ONLY}
        label={getLabel(READ_ONLY)}
        saving={saving === READ_ONLY}
        selected={selected === READ_ONLY}
        description={
          <PermissionSelectOptionDescriptions>
            <PermissionDescription allowed={true} permission="view builds" />
            <PermissionDescription allowed={false} permission="create builds" />
            <PermissionDescription allowed={false} permission="edit pipeline settings" />
          </PermissionSelectOptionDescriptions>
        }
      />
    </Chooser>
  </Dropdown>
);

const AccessLevel = ({ saving, teamPipeline, onAccessLevelChange }: AccessLevelProps) => {
  if (teamPipeline.permissions.teamPipelineUpdate.allowed) {
    return (
      <DropdownWithChooser
        saving={saving}
        selected={teamPipeline.accessLevel}
        onAccessLevelChange={onAccessLevelChange}
      />
    );
  }

  return <span className="dark-gray">{getLabel(teamPipeline.accessLevel)}</span>;
};

export default AccessLevel;
